import api from './api';
import { transformPaginatedResponse } from './transformers';

import type {
  ActiveTenantReportSummary,
  CallReportListItem,
  CampaignReportListItem,
  UserActivityReportListItem,
} from '../types';


export interface CampaignReportsListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  campaign?: string;
  search?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface CampaignReportCampaignOption {
  id: string;
  name: string;
}

export interface CallReportsListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateCallReportPayload {
  name?: string;
  description?: string;
  filtersPayload?: Record<string, string>;
}

export interface UserActivityReportsListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface CreateUserActivityReportPayload {
  name?: string;
  description?: string;
  filtersPayload?: Record<string, string>;
}

type RecordLike = Record<string, unknown>;

function isRecordLike(value: unknown): value is RecordLike {
  return typeof value === 'object' && value !== null;
}

function normalizeActiveTenantReport(value: unknown): ActiveTenantReportSummary | null {
  if (!isRecordLike(value)) {
    return null;
  }
  const reportType = value.reportType ?? value.report_type;
  const reportId = value.reportId ?? value.report_id;
  const status = value.status;
  const reportsTab = value.reportsTab ?? value.reports_tab;
  const reportsPath = value.reportsPath ?? value.reports_path;
  if (
    (reportType !== 'campaign' && reportType !== 'call' && reportType !== 'user_activity')
    || typeof reportId !== 'string'
    || (status !== 'pending' && status !== 'processing')
    || (reportsTab !== 'campaigns' && reportsTab !== 'calls' && reportsTab !== 'users')
    || typeof reportsPath !== 'string'
  ) {
    return null;
  }
  return {
    reportType,
    reportId,
    status,
    name: typeof value.name === 'string' ? value.name : '',
    createdByName:
      typeof (value.createdByName ?? value.created_by_name) === 'string'
        ? String(value.createdByName ?? value.created_by_name)
        : '',
    startedAt:
      typeof (value.startedAt ?? value.started_at) === 'string'
        ? String(value.startedAt ?? value.started_at)
        : null,
    campaignId:
      typeof (value.campaignId ?? value.campaign_id) === 'string'
        ? String(value.campaignId ?? value.campaign_id)
        : null,
    campaignName:
      typeof (value.campaignName ?? value.campaign_name) === 'string'
        ? String(value.campaignName ?? value.campaign_name)
        : '',
    reportsTab,
    reportsPath,
  };
}

function getErrorResponseData(error: unknown): RecordLike | null {
  if (!isRecordLike(error)) {
    return null;
  }
  const response = error.response;
  if (!isRecordLike(response)) {
    return null;
  }
  return isRecordLike(response.data) ? response.data : null;
}

export function extractActiveTenantReportFromError(error: unknown): ActiveTenantReportSummary | null {
  const responseData = getErrorResponseData(error);
  return normalizeActiveTenantReport(responseData?.activeReport ?? responseData?.active_report);
}

export function extractApiDetailMessage(error: unknown): string | null {
  const responseData = getErrorResponseData(error);
  if (typeof responseData?.detail === 'string' && responseData.detail.trim()) {
    return responseData.detail;
  }
  return null;
}


export async function fetchCampaignReports(params: CampaignReportsListParams = {}) {
  const query: Record<string, unknown> = {};
  if (params.page !== undefined) {
    query.page = params.page;
  }
  if (params.pageSize !== undefined) {
    query.page_size = params.pageSize;
  }
  if (params.ordering) {
    query.ordering = params.ordering;
  }
  if (params.campaign) {
    query.campaign = params.campaign;
  }
  if (params.search) {
    query.search = params.search;
  }
  if (params.status) {
    query.status = params.status;
  }
  if (params.startDate) {
    query.start_date = params.startDate;
  }
  if (params.endDate) {
    query.end_date = params.endDate;
  }

  const response = await api.get('/api/reports/campaigns/', { params: query });
  return transformPaginatedResponse<CampaignReportListItem>(response.data);
}

export async function fetchCampaignReportCampaignOptions(params: {
  search?: string;
  selected?: string;
  limit?: number;
} = {}) {
  const query: Record<string, unknown> = {};
  if (params.search) {
    query.search = params.search;
  }
  if (params.selected) {
    query.selected = params.selected;
  }
  if (params.limit !== undefined) {
    query.limit = params.limit;
  }

  const response = await api.get('/api/reports/campaign-options/', { params: query });
  return response.data as CampaignReportCampaignOption[];
}

export async function fetchCallReports(params: CallReportsListParams = {}) {
  const query: Record<string, unknown> = {};
  if (params.page !== undefined) {
    query.page = params.page;
  }
  if (params.pageSize !== undefined) {
    query.page_size = params.pageSize;
  }
  if (params.ordering) {
    query.ordering = params.ordering;
  }
  if (params.status) {
    query.status = params.status;
  }
  if (params.startDate) {
    query.start_date = params.startDate;
  }
  if (params.endDate) {
    query.end_date = params.endDate;
  }

  const response = await api.get('/api/reports/calls/', { params: query });
  return transformPaginatedResponse<CallReportListItem>(response.data);
}

export async function fetchActiveTenantReport() {
  const response = await api.get('/api/reports/active/');
  return normalizeActiveTenantReport(response.data?.activeReport ?? response.data?.active_report);
}

export async function createCallReport(payload: CreateCallReportPayload) {
  const response = await api.post('/api/reports/calls/', {
    name: payload.name,
    description: payload.description,
    filters_payload: payload.filtersPayload ?? {},
  });
  return response.data as CallReportListItem;
}

export async function fetchUserActivityReports(params: UserActivityReportsListParams = {}) {
  const query: Record<string, unknown> = {};
  if (params.page !== undefined) {
    query.page = params.page;
  }
  if (params.pageSize !== undefined) {
    query.page_size = params.pageSize;
  }
  if (params.ordering) {
    query.ordering = params.ordering;
  }
  if (params.status) {
    query.status = params.status;
  }
  if (params.startDate) {
    query.start_date = params.startDate;
  }
  if (params.endDate) {
    query.end_date = params.endDate;
  }

  const response = await api.get('/api/reports/users/', { params: query });
  return transformPaginatedResponse<UserActivityReportListItem>(response.data);
}

export async function createUserActivityReport(payload: CreateUserActivityReportPayload) {
  const response = await api.post('/api/reports/users/', {
    name: payload.name,
    description: payload.description,
    filters_payload: payload.filtersPayload ?? {},
  });
  return response.data as UserActivityReportListItem;
}

function extractDownloadUrl(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  const data = payload as Record<string, unknown>;
  const candidate = data.url ?? data.downloadUrl ?? data.download_url;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : null;
}

export async function requestCampaignReportDownload(reportId: string) {
  const response = await api.post(`/api/reports/campaigns/${reportId}/download/`);
  return extractDownloadUrl(response.data);
}

export async function requestCallReportDownload(reportId: string) {
  const response = await api.post(`/api/reports/calls/${reportId}/download/`);
  return extractDownloadUrl(response.data);
}

export async function requestUserActivityReportDownload(reportId: string) {
  const response = await api.post(`/api/reports/users/${reportId}/download/`);
  return extractDownloadUrl(response.data);
}
